import PropTypes from "prop-types";
import { constants } from "../../../../../constants/pageConstants/pdpConstants";
import { updateCurrency } from "../../../../../utils/common";
import Drawer from "../../../../molecules/drawer";
import Typography from "../../../../atoms/typography";
import CustomImage from "../../../../atoms/customImage";
import ButtonComponent from "../../../../atoms/button";

/**
 * SurgePriceDrawer Component
 *
 * A reusable drawer component for displaying surge pricing details.
 *
 * @component
 * @param {boolean} isOpen - Controls whether the drawer is open or closed.
 * @param {function} onClose - Callback function to handle drawer close event.
 * @param {string} surgePricingLabel - The label for the surge pricing heading.
 * @param {object} surgePriceDetails - Contains the notification message and price details.
 * @param {string} selectedCurrency - The selected currency code.
 * @param {function} onContinue - Function to handle the continue button click event.
 */
const SurgePriceDrawer = ({
  isOpen,
  onClose,
  surgePriceDetails,
  selectedCurrency,
  updatedDeliveryDatesAndGeoPrice,
  onContinue,
}) => {
  return (
    <Drawer position="bottom" isOpen={isOpen} onDrawerClose={onClose}>
      <div className="mx-auto w-full max-w-[400px]">
        <div className="flex items-start gap-10 rounded-t-[16px] border-b border-thinLightGrey bg-white-900 px-16 pb-12 pt-6">
          <Typography Tag="p" classes="font-sans text-[16px] font-semibold capitalize leading-[24.4px] text-black-100">
            {constants.SURGE_PRICING}
          </Typography>
        </div>

        <Typography
          Tag="p"
          classes="text-black-100 flex items-center font-sans text-[14px] font-medium leading-[21px] pt-[16px] px-[16px] pb-[8px] bg-white-900"
        >
          {surgePriceDetails?.notificationMessage}
        </Typography>

        <div className="mb-[16px] ml-[16px] flex w-fit items-center gap-12 rounded-[24px] bg-fnp-50 p-[12px_16px]">
          <Typography Tag="p" classes="text-[16px] text-grey-400 font-inter font-normal line-through">
            {updateCurrency(selectedCurrency, updatedDeliveryDatesAndGeoPrice?.listPrice) || 0}
          </Typography>
          <CustomImage width={16} height={16} src="/icons/price-surge-icon.svg" alt="price_surge_icon" />
          <Typography Tag="p" classes="text-[16px] text-black-100 font-inter font-semibold leading-[24px]">
            {updateCurrency(selectedCurrency, updatedDeliveryDatesAndGeoPrice?.price) || 0}
          </Typography>
        </div>

        <div className="bg-white flex items-start gap-[16px] border-t border-lightGrey p-[16px_20px_0px_20px]">
          <ButtonComponent
            size="small"
            classes="text-base flex justify-center"
            buttonVariant="filled"
            label="Continue"
            onClick={onContinue}
            isRemoveExtraPadding={true}
          />
        </div>
      </div>
    </Drawer>
  );
};

SurgePriceDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  surgePriceDetails: PropTypes.shape({
    notificationMessage: PropTypes.string,
  }).isRequired,
  selectedCurrency: PropTypes.object,
  updatedDeliveryDatesAndGeoPrice: PropTypes.shape({
    listPrice: PropTypes.number,
    price: PropTypes.number,
  }).isRequired,
  onContinue: PropTypes.func.isRequired,
};

export default SurgePriceDrawer;
